import { Link, useParams } from 'react-router-dom';
import type { Topic } from '@/types/content';
import { topics } from '@/mocks/topics';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import TopicCard from '@/components/feature/TopicCard';
import { useSeo } from '@/hooks/useSeo';
import { formatDate, formatHeat } from '@/utils/format';

const allTopics = topics as Topic[];

export default function TopicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const topic = allTopics.find((item) => item.id === id);

  const related = allTopics
    .filter((item) => item.id !== id)
    .sort((a, b) => {
      const aSame = a.category === topic?.category ? 1 : 0;
      const bSame = b.category === topic?.category ? 1 : 0;
      return bSame - aSame || b.heat - a.heat;
    })
    .slice(0, 3);

  const hotTags = Array.from(new Set(allTopics.flatMap((item) => item.tags))).slice(0, 10);

  useSeo({
    title: topic ? `${topic.title} - 511群众围观记录` : '瓜点未找到 - 511群众',
    description: topic
      ? `${topic.summary}（511群众 · ${topic.categoryLabel}，吃瓜小队蹲点汇总）`
      : '这条瓜点记录暂时找不到了，去围观前线看看其他内容。',
    keywords: topic
      ? `${topic.title},511群众,${topic.tags.join(',')},吃瓜小队,围观前线`
      : '511群众,围观前线,吃瓜小队',
    jsonLd: topic
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: topic.title,
            description: topic.summary,
            datePublished: topic.date,
            inLanguage: 'zh-CN',
            articleSection: topic.categoryLabel,
            keywords: topic.tags.join(','),
            author: { '@type': 'Organization', name: '511群众' },
            publisher: { '@type': 'Organization', name: '511群众' },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://511qunzhong.space/' },
              { '@type': 'ListItem', position: 2, name: '围观前线', item: 'https://511qunzhong.space/frontline' },
              { '@type': 'ListItem', position: 3, name: topic.title },
            ],
          },
        ]
      : undefined,
  });

  if (!topic) {
    return (
      <SiteLayout>
        <section className="w-full px-4 md:px-8 lg:px-12 py-24 flex flex-col items-center text-center">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-3xl">
            <i className="ri-file-search-line"></i>
          </span>
          <h1 className="mt-5 font-heading font-bold text-2xl md:text-3xl text-foreground-950">
            这条瓜点暂时找不到了
          </h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已被更正、合并，或者链接不太对。</p>
          <Link
            to="/frontline"
            className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-radar-line"></i>
            回到围观前线
          </Link>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <article className="w-full">
        <header className="w-full px-4 md:px-8 lg:px-12 pt-8 md:pt-10">
          <Breadcrumbs
            items={[
              { label: '围观前线', to: '/frontline' },
              { label: topic.categoryLabel, to: `/frontline?category=${topic.category}` },
              { label: topic.title },
            ]}
          />
        </header>

        <div className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10 lg:gap-14">
            <div className="min-w-0">
              <Link
                to={`/frontline?category=${topic.category}`}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium hover:bg-secondary-200 transition-colors"
              >
                <i className="ri-price-tag-3-line"></i>
                {topic.categoryLabel}
              </Link>

              <h1 className="mt-4 font-heading font-black text-2xl md:text-4xl leading-snug text-foreground-950">
                {topic.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
                <span className="flex items-center gap-2">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-800">
                    <i className="ri-user-smile-line"></i>
                  </span>
                  {topic.author}
                </span>
                <time dateTime={topic.date} className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {formatDate(topic.date)}
                </time>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-primary-500"></i>
                  热度 {formatHeat(topic.heat)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-chat-3-line"></i>
                  {topic.comments} 条讨论
                </span>
              </div>

              <div className="mt-6 w-full rounded-lg overflow-hidden bg-background-200">
                <div className="w-full h-60 md:h-[420px]">
                  <img
                    src={topic.image}
                    alt={`${topic.title}现场记录配图`}
                    title={`${topic.title} · 511群众围观记录`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="mt-8 space-y-5">
                {topic.content.map((paragraph, index) => (
                  <p key={index} className="text-[15px] md:text-base leading-[1.9] text-foreground-800">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-8 rounded-lg border border-background-200/80 bg-background-100/60 p-4 md:p-5">
                <p className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-600">
                  <span className="w-5 h-5 flex items-center justify-center text-secondary-600 shrink-0 mt-0.5">
                    <i className="ri-information-line"></i>
                  </span>
                  本文内容来自公开场合的现场见闻整理，仅作记录，不代表对任何一方的判断。如有出入，欢迎通过页面底部留言告知。
                </p>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                {topic.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-background-200 text-foreground-700 text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <aside className="space-y-6">
              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <h2 className="font-heading font-bold text-base text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                    <i className="ri-links-line"></i>
                  </span>
                  相关推荐
                </h2>
                <ul className="space-y-4">
                  {related.map((item) => (
                    <li key={item.id}>
                      <Link to={`/topic/${item.id}`} className="group block">
                        <span className="text-sm font-medium text-foreground-900 leading-snug group-hover:text-primary-700 transition-colors line-clamp-2">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-500">
                          {item.categoryLabel} · 热度 {formatHeat(item.heat)}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
                <h2 className="font-heading font-bold text-base text-foreground-950 mb-4 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-primary-600">
                    <i className="ri-price-tag-3-line"></i>
                  </span>
                  热门标签
                </h2>
                <div className="flex flex-wrap gap-2">
                  {hotTags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/frontline?keyword=${encodeURIComponent(tag)}`}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-700 text-xs hover:bg-primary-100 hover:text-primary-800 transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-lg bg-foreground-950 p-5 text-background-100">
                <h2 className="font-heading font-bold text-base text-white mb-2">想看更多现场？</h2>
                <p className="text-sm text-background-300 leading-relaxed">
                  围观前线汇总了吃瓜小队的全部记录，按主题就能找到你关心的那类内容。
                </p>
                <Link
                  to="/frontline"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent-400 hover:text-accent-300 transition-colors"
                >
                  前往围观前线
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </aside>
          </div>
        </div>

        <section className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-background-100">
          <div className="flex items-end justify-between gap-4 mb-6">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">继续围观</h2>
            <Link
              to="/frontline"
              className="text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors whitespace-nowrap"
            >
              查看全部
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {related.map((item) => (
              <TopicCard key={item.id} topic={item} />
            ))}
          </div>
        </section>
      </article>
    </SiteLayout>
  );
}